import React, { useState } from 'react';
import { UserData } from '../types';
import { CloseIcon } from './Icons';

interface FollowListModalProps {
    isOpen: boolean;
    onClose: () => void;
    listType: 'followers' | 'following' | 'friends' | 'connections' | null;
    user: { id: string, data: UserData } | null;
    usersData: Record<string, UserData>;
    onNavigateToProfile: (userId: string) => void;
    /** If provided, only the current logged-in user can see the connections view */
    currentUser?: string;
}

type ConnectionsTab = 'followers' | 'following';

export const FollowListModal = ({ isOpen, onClose, listType, user, usersData, onNavigateToProfile, currentUser }: FollowListModalProps) => {
    const [connectionsTab, setConnectionsTab] = useState<ConnectionsTab>('followers');

    if (!isOpen || !user) return null;

    const isConnections = listType === 'connections';
    const isFriends = listType === 'friends';

    // Only the owner can see the connections view
    const canSeeConnections = isConnections && currentUser === user.id;

    const isCustomNickname = user?.data.followerNickname && user.data.followerNickname.trim() !== '' && user.data.followerNickname.toLowerCase() !== 'followers';
    const friendsLabel = isCustomNickname ? user.data.followerNickname! : 'Friends';
    const followersLabel = isCustomNickname ? user.data.followerNickname! : 'Followers';

    let list: string[] = [];
    let title = '';

    if (isFriends) {
        list = user.data.friends || [];
        title = friendsLabel;
    } else if (isConnections && canSeeConnections) {
        const friends = new Set(user.data.friends || []);
        if (connectionsTab === 'followers') {
            // Non-friend followers only
            list = (user.data.followers || []).filter(f => !friends.has(f));
            title = followersLabel;
        } else {
            // Non-friend following only
            list = (user.data.following || []).filter(f => !friends.has(f));
            title = 'Following';
        }
    } else if (listType === 'followers') {
        list = user.data.followers || [];
        title = followersLabel;
    } else {
        list = user.data.following || [];
        title = 'Following';
    }

    const handleNavigate = (userId: string) => {
        onClose();
        setTimeout(() => {
            onNavigateToProfile(userId);
        }, 50);
    };

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[70]" onClick={onClose}>
            <div
                className="rounded-2xl shadow-lg p-6 w-full mx-4 h-[65vh] flex flex-col"
                style={{ backgroundColor: 'var(--app-theme-bg)', fontFamily: user?.data.appFontFamily || undefined }}
                onClick={e => e.stopPropagation()}
            >
                <div className="flex justify-between items-center mb-4 flex-shrink-0">
                    <h2 className="text-xl font-bold">
                        {isConnections && canSeeConnections ? 'Connections' : title} ({list.length})
                    </h2>
                    <button onClick={onClose} className="p-1 rounded-full hover:bg-white/10"><CloseIcon className="w-5 h-5" /></button>
                </div>

                {/* Connections tabs — owner only */}
                {isConnections && canSeeConnections && (
                    <div className="flex gap-2 mb-4 flex-shrink-0">
                        {(['followers', 'following'] as ConnectionsTab[]).map(tab => (
                            <button
                                key={tab}
                                onClick={() => setConnectionsTab(tab)}
                                className={`px-4 py-1.5 rounded-full text-xs font-bold capitalize transition-all ${connectionsTab === tab ? 'bg-white/20 text-white' : 'bg-white/5 text-white/50 hover:text-white'}`}
                            >
                                {tab === 'followers' ? followersLabel : 'Following'}
                            </button>
                        ))}
                    </div>
                )}

                <div className="flex-grow overflow-y-auto allow-scroll">
                    {list.length === 0 ? (
                        <p className="text-gray-400 text-center mt-8">No users to show.</p>
                    ) : (
                        <ul className="space-y-3">
                            {list.map(username => (
                                <li key={username}>
                                    <button
                                        onClick={() => handleNavigate(username)}
                                        className="w-full text-left flex items-center gap-4 p-2 hover:bg-white/10 transition-colors rounded-lg"
                                    >
                                        <img src={usersData[username]?.pfp} alt={username} className="w-12 h-12 rounded-full object-cover" />
                                        <div>
                                            <span className="font-bold">{usersData[username]?.displayName || username}</span>
                                            <p className="text-sm text-gray-400">@{username.toLowerCase()}</p>
                                        </div>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
};
